// src/app/videos/EpisodePlayer.tsx
//
// Click-to-load facade for one video. The cover is a static image and the YouTube
// iframe is only mounted once someone asks for it — an autoloading embed on every card
// would drag the page down for the (many) visitors who came to read.

'use client'

import { useState } from 'react'
import Image from 'next/image'
import type { VideoSummary } from '@/lib/content/video-catalog'

/**
 * Takes ONLY what it renders. This is a client component, so whatever it receives is
 * serialised into the RSC payload and shipped to the browser — including to anonymous
 * visitors on the soft-gated video page. Passing the full Video put every gated worked
 * problem and the takeaway into view-source (found by a probe: the takeaway appeared
 * exactly once in the HTML — never rendered, so no DOM copy — only as a prop). Keep this
 * type minimal so the gate cannot be bypassed by reading the page source.
 */
export type PlayerVideo = Pick<VideoSummary, 'youtubeId' | 'title' | 'cover' | 'n'>

export default function EpisodePlayer({
  video,
  autoplay = true,
}: {
  video: PlayerVideo
  autoplay?: boolean
}) {
  const [playing, setPlaying] = useState(false)

  return (
    /* 9:16 — these are vertical Shorts. Capped in width so a portrait video does not
       become a skyscraper on a desktop screen. */
    <div className="relative mx-auto aspect-[9/16] w-full max-w-[320px] overflow-hidden rounded-xl border border-border bg-muted">
      {playing ? (
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${video.youtubeId}?rel=0${autoplay ? '&autoplay=1' : ''}`}
          title={video.title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      ) : (
        <>
          <Image
            src={video.cover}
            alt={`Episode ${video.n}: ${video.title}`}
            fill
            sizes="320px"
            priority
            className="object-cover"
          />
          <button
            type="button"
            onClick={() => setPlaying(true)}
            aria-label={`Play episode ${video.n}: ${video.title}`}
            className="group absolute inset-0 flex items-center justify-center bg-black/10 transition hover:bg-black/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand/90 text-on-brand shadow-lg transition group-hover:scale-110">
              <svg
                className="h-7 w-7 translate-x-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11v11.78a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </span>
          </button>
        </>
      )}
    </div>
  )
}
